import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  Store,
  createSelector,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  Activity,
  ActivityRow,
  ActivitySelectionListItem,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  GroupedUnassignedUserDataV2,
  Job,
  NumberOfShiftPerDayRecord,
  Service,
  ShiftTemplate,
  TimelineTimeframe,
  UnassignedShift,
} from '@wilson/interfaces';
import { TabEnum } from '@wilson/shift-timeline/services';
import { cloneDeep } from 'lodash';
import {
  Observable,
  Subject,
  catchError,
  from,
  map,
  takeUntil,
  tap,
  switchMap,
} from 'rxjs';
import {
  ShiftId,
  ShiftTimelineUnassignedRegionStateModel,
  UnassignedDateToShiftRecords,
} from '../interfaces';
import { ActivitiesStateLoaderService } from '../services/activities-state-loader.service';
import {
  AddActivityToUnassignedRegion,
  AddShiftTemplateToSuggestionList,
  AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  AddShiftWithoutActivitiesToUnassignedRegion,
  BulkAddShiftActivitiesToUnassignedRegion,
  BulkDeleteShiftActivitiesFromUnassignedRegion,
  ClearAllUnassignedActivitiesRelatedData,
  ClearShiftActivitiesRows,
  ClearUnassignedShiftsWithoutActivities,
  FetchActivitiesForUnassignedShifts,
  HideLoaderWhenLoadingUnassignedShiftsOrActivities,
  RemoveActivityFromUnassignedRegion,
  RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  RemoveShiftWithoutActivitiesFromUnassignedRegion,
  SetSelectedUnassignedRegion,
  TimelineFetchUnassignedShiftsWithoutActivities,
  TimelineRemoveActivityFromUnassignedRegion,
  TimelineUpdateUnassignedRegionCollapsedViewShiftsCount,
  TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities,
  TimelineUpdateStandardViewUnassignedShiftsRegionWithoutActivities,
  TimelineUpdateUnassignedActivities,
  TimelineUpdateUnassignedShiftsRegionWithoutActivities,
  TimelineUpsertActivityToUnassignedRegion,
  ToggleExpandUnassignedRegion,
  UpdateShiftWithoutActivitiesFromCellViewUnassignedRegion,
  UpdateShiftWithoutActivitiesFromUnassignedRegion,
} from './shift-timeline-unassigned-region.actions';
import { TimelineFetchUnassignedActivitiesActionService } from './timeline-fetch-unassigned-activities-action.service';
import { getStartDatetime } from '@wilson/non-domain-specific/overlap-helpers';
import { determineShiftRenderDatetime } from '@wilson/shift-timeline/services';
import { UserTimelinesGateway } from '@wilson/api/gateway';
import { ShiftTimelineFilterStateService } from '../shift-timeline-filters/shift-timeline-filter-state.service';
import { getTimeZoneCorrectedDateRange } from '@wilson/utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import { toNumberOfShiftPerDayRecordKey } from '@wilson/non-domain-specific/shift-helpers/pipes';

const defaults: ShiftTimelineUnassignedRegionStateModel = {
  loadingActivitiesForShiftIds: [],
  selectedRegion: TabEnum.UnassignedShifts,
  shiftActivitiesRows: {},
  unassignedShiftsWithoutActivities: {},
  suggestedShiftTemplates: [],
  unassignedActivities: [],
  unassignedServices: [],
  unassignedJobs: [],
  isLoadingUnassignedShifts: false,
  unassignedActivitiesViewRows: [],
  hideLoaderWhenLoadingUnassignedShiftsOrActivities: false,
  isLoadingUnassignedActivities: false,
  isUnassignedRegionExpanded: false,
  isRefreshingUnassignedShifts: false,
  isRefreshingUnassignedActivities: false,
  unassignedDateToShiftRecords: {},
  numberOfShiftPerDayRecord: {},
};

@State<ShiftTimelineUnassignedRegionStateModel>({
  name: 'shiftTimelineUnassignedRegion',
  defaults,
})
@Injectable()
export class ShiftTimelineUnassignedRegionState {
  private activitiesStateLoaderService = inject(ActivitiesStateLoaderService);

  private unsubRequestForUnassignedShiftsSubject = new Subject();
  private store = inject(Store);
  private toastrService = inject(NzMessageService);
  private translateService = inject(TranslateService);
  private timelineFetchUnassignedActivitiesActionService = inject(
    TimelineFetchUnassignedActivitiesActionService,
  );
  private userTimelinesGateway = inject(UserTimelinesGateway);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );

  @Selector()
  static isRefreshingUnassignedShifts(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.isRefreshingUnassignedShifts;
  }

  @Selector()
  static isRefreshingUnassignedActivities(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.isRefreshingUnassignedActivities;
  }

  @Selector()
  static unassignedShiftsWithoutActivities(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): Record<string, UnassignedShift> {
    return state.unassignedShiftsWithoutActivities;
  }

  @Selector()
  static unassignedDateToShiftRecords(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): UnassignedDateToShiftRecords {
    return state.unassignedDateToShiftRecords;
  }

  @Selector()
  static numberOfShiftPerDayRecords(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): NumberOfShiftPerDayRecord {
    return state.numberOfShiftPerDayRecord;
  }

  @Selector()
  static suggestedShiftTemplates(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): ShiftTemplate[] {
    return state.suggestedShiftTemplates;
  }

  @Selector()
  static unassignedActivities(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): Activity[] {
    return state.unassignedActivities;
  }

  @Selector()
  static unassignedActivitiesLength(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): number {
    return state.unassignedActivities.length;
  }

  @Selector()
  static isUnassignedRegionExpanded(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.isUnassignedRegionExpanded;
  }

  @Selector()
  static isLoadingUnassignedShifts(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.isLoadingUnassignedShifts;
  }

  @Selector()
  static hideLoaderWhenLoadingUnassignedShiftsOrActivities(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.hideLoaderWhenLoadingUnassignedShiftsOrActivities;
  }

  @Selector()
  static isLoadingUnassignedActivities(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): boolean {
    return state.isLoadingUnassignedActivities;
  }

  @Selector()
  static unassignedActivitiesViewRows(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): GroupedUnassignedUserDataV2[] {
    return state.unassignedActivitiesViewRows;
  }

  @Selector()
  static unassignedServices(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): Service[] {
    return state.unassignedServices;
  }

  @Selector()
  static unassignedJobs(state: ShiftTimelineUnassignedRegionStateModel): Job[] {
    return state.unassignedJobs;
  }

  @Selector()
  static shiftActivitiesRows(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): Record<string, ActivityRow[]> {
    return state.shiftActivitiesRows;
  }

  @Selector()
  static selectedRegion(
    state: ShiftTimelineUnassignedRegionStateModel,
  ): TabEnum {
    return state.selectedRegion;
  }

  static shiftActivitiesRowsOf(
    shiftId: string,
  ): (rowsMap: Record<string, ActivityRow[]>) => ActivityRow[] {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.shiftActivitiesRows],
      (rowsMap) => {
        return rowsMap[shiftId] ?? [];
      },
    );
  }

  static unassignedShiftActivitiesArray(
    shiftId: string,
  ): (
    activityRows: ActivityRow[],
  ) => ActivityWithCategoryAndLocationsAndReportedDetails[] {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.shiftActivitiesRowsOf(shiftId)],
      (activityRows: ActivityRow[]) => {
        return activityRows.flatMap((row) => (row ? Object.values(row) : []));
      },
    );
  }

  static getService(
    serviceId: string,
  ): (unassignedServices: Service[]) => Service | null {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.unassignedServices],
      (unassignedServices) => {
        const service = unassignedServices.find(
          (service) => service.id === serviceId,
        );
        return service ? service : null;
      },
    );
  }

  static getServiceName(
    serviceId: string,
  ): (unassignedServices: Service[]) => string {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.unassignedServices],
      (unassignedServices) => {
        const service = unassignedServices.find(
          (service) => service.id === serviceId,
        );
        return service ? service.name : '';
      },
    );
  }

  static getJobName(jobId: string): (unassignedJobs: Job[]) => string {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.unassignedJobs],
      (unassignedJobs) => {
        const job = unassignedJobs.find((job) => job.id === jobId);
        return job ? job.name : '';
      },
    );
  }

  static getShiftWithoutActivities(
    shiftId: string,
  ): (
    unassignedShifts: Record<string, UnassignedShift>,
  ) => UnassignedShift | undefined {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.unassignedShiftsWithoutActivities],
      (unassignedShifts) => {
        return Object.values(unassignedShifts).find(
          (shift) => shift.id === shiftId,
        );
      },
    );
  }

  static getCellViewShiftWithoutActivities(
    shiftId: string,
  ): (
    unassignedDateToShiftRecords: UnassignedDateToShiftRecords,
  ) => UnassignedShift | undefined {
    return createSelector(
      [ShiftTimelineUnassignedRegionState.unassignedDateToShiftRecords],
      (unassignedDateToShiftRecords) => {
        return this.getShiftFromUnassignedDateRecords(
          unassignedDateToShiftRecords,
          shiftId,
        );
      },
    );
  }

  static getCellViewShiftWithoutActivitiesCount(
    day: Date,
  ): (numberOfShiftPerDayRecords: NumberOfShiftPerDayRecord) => number {
    const startDateTime = toNumberOfShiftPerDayRecordKey(day);

    return createSelector(
      [ShiftTimelineUnassignedRegionState.numberOfShiftPerDayRecords],
      (numberOfShiftPerDayRecords) => {
        return numberOfShiftPerDayRecords[startDateTime] || 0;
      },
    );
  }

  @Action(ClearShiftActivitiesRows)
  clearDeterminedShiftActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
  ): void {
    ctx.patchState({
      shiftActivitiesRows: {},
    });
  }

  @Action(ClearUnassignedShiftsWithoutActivities)
  clearUnassignedShiftsWithoutActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
  ): void {
    ctx.patchState({
      unassignedShiftsWithoutActivities: {},
    });
  }

  @Action(ClearAllUnassignedActivitiesRelatedData)
  clearAllUnassignedActivitiesRelatedData(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
  ): void {
    ctx.patchState({
      unassignedActivities: [],
      unassignedJobs: [],
      unassignedServices: [],
      unassignedActivitiesViewRows: [],
      unassignedDateToShiftRecords: {},
      numberOfShiftPerDayRecord: {},
    });
  }

  @Action(SetSelectedUnassignedRegion)
  setUnassignedRegion(
    { patchState }: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    { payload }: SetSelectedUnassignedRegion,
  ): void {
    patchState({
      selectedRegion: payload,
    });
  }

  @Action(ToggleExpandUnassignedRegion)
  toggleExpandUnassignedRegion(
    { patchState }: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    { payload }: ToggleExpandUnassignedRegion,
  ): void {
    const updatePayload: Pick<
      ShiftTimelineUnassignedRegionStateModel,
      'isUnassignedRegionExpanded'
    > = {
      isUnassignedRegionExpanded: payload,
    };
    patchState(updatePayload);
  }

  @Action(HideLoaderWhenLoadingUnassignedShiftsOrActivities)
  hideLoaderWhenLoadingUnassignedShiftsOrActivities(
    { patchState }: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    { payload }: HideLoaderWhenLoadingUnassignedShiftsOrActivities,
  ): void {
    const updatePayload: Pick<
      ShiftTimelineUnassignedRegionStateModel,
      'hideLoaderWhenLoadingUnassignedShiftsOrActivities'
    > = {
      hideLoaderWhenLoadingUnassignedShiftsOrActivities: payload,
    };
    patchState(updatePayload);
  }

  @Action(TimelineFetchUnassignedShiftsWithoutActivities)
  fetchUnassignedShiftsWithoutActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineFetchUnassignedShiftsWithoutActivities,
  ): Observable<Record<string, UnassignedShift>> {
    const timeFrame = action.timeFrame;
    ctx.patchState({
      isLoadingUnassignedShifts: true,
      isUnassignedRegionExpanded: false,
      hideLoaderWhenLoadingUnassignedShiftsOrActivities: false,
    });

    return this.fetchUnassignedShiftsAndPrepareDataForStandardView(
      timeFrame,
    ).pipe(
      tap((transformedShifts) => {
        ctx.patchState({
          unassignedShiftsWithoutActivities: transformedShifts,
          isLoadingUnassignedShifts: false,
          hideLoaderWhenLoadingUnassignedShiftsOrActivities: false,
        });
      }),
      catchError(() => {
        this.toastrService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  @Action(TimelineUpdateUnassignedShiftsRegionWithoutActivities)
  updateUnassignedActivitiesWithoutActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateUnassignedShiftsRegionWithoutActivities,
  ): Observable<Record<string, UnassignedShift>> {
    ctx.patchState({
      isRefreshingUnassignedShifts: true,
    });
    return this.fetchUnassignedShiftsAndPrepareDataForStandardView(
      action.timeFrame,
    ).pipe(
      tap((transformedShifts) => {
        ctx.setState(
          patch({
            unassignedShiftsWithoutActivities: transformedShifts,
            isLoadingUnassignedShifts: false,
            isRefreshingUnassignedShifts: false,
          }),
        );
      }),
      catchError(() => {
        this.toastrService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  @Action(TimelineUpdateUnassignedRegionCollapsedViewShiftsCount)
  timelineUpdateUnassignedRegionCollapsedViewShiftsCount(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateUnassignedRegionCollapsedViewShiftsCount,
  ): Observable<NumberOfShiftPerDayRecord> {
    ctx.patchState({
      isRefreshingUnassignedShifts: true,
    });
    return this.fetchUnassignedShiftsCountPerDay(action.timeFrame).pipe(
      tap((numberOfShiftPerDayRecord) => {
        ctx.setState(
          patch({
            numberOfShiftPerDayRecord,
            isLoadingUnassignedShifts: false,
            isRefreshingUnassignedShifts: false,
          }),
        );
      }),
      catchError(() => {
        this.toastrService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  @Action(TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities)
  timelineUpdateCellViewUnassignedShiftsRegionWithoutActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateCellViewUnassignedShiftsRegionWithoutActivities,
  ): Observable<UnassignedDateToShiftRecords> {
    ctx.patchState({
      isRefreshingUnassignedShifts: true,
    });
    return this.fetchUnassignedShiftsAndPrepareDataForCellView(
      action.timeFrame,
    ).pipe(
      tap((unassignedDateToShiftRecords) => {
        ctx.setState(
          patch({
            unassignedDateToShiftRecords,
            isLoadingUnassignedShifts: false,
            isRefreshingUnassignedShifts: false,
          }),
        );
      }),
      catchError(() => {
        this.toastrService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  @Action(TimelineUpdateStandardViewUnassignedShiftsRegionWithoutActivities)
  timelineUpdateStandardViewUnassignedShiftsRegionWithoutActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateUnassignedShiftsRegionWithoutActivities,
  ): Observable<Record<string, UnassignedShift>> {
    ctx.patchState({
      isRefreshingUnassignedShifts: true,
    });
    return this.fetchUnassignedShiftsAndPrepareDataForStandardView(
      action.timeFrame,
    ).pipe(
      tap((transformedShifts) => {
        ctx.setState(
          patch({
            unassignedShiftsWithoutActivities: transformedShifts,
            isLoadingUnassignedShifts: false,
            isRefreshingUnassignedShifts: false,
          }),
        );
      }),
      catchError(() => {
        this.toastrService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
        return [];
      }),
    );
  }

  @Action(TimelineUpdateUnassignedActivities)
  timelineUpdateUnassignedActivities(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpdateUnassignedActivities,
  ): Observable<ActivitySelectionListItem[]> {
    return this.timelineFetchUnassignedActivitiesActionService.handleUpdate(
      ctx,
      action,
    );
  }

  @Action(UpdateShiftWithoutActivitiesFromCellViewUnassignedRegion)
  updateCellViewShiftWithoutActivitiesFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: UpdateShiftWithoutActivitiesFromCellViewUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const { startDatetime } = determineShiftRenderDatetime(action.shift);
    const startDateTime = toNumberOfShiftPerDayRecordKey(
      new Date(startDatetime.date),
    );

    if (startDateTime in state.unassignedDateToShiftRecords) {
      ctx.setState(
        patch({
          unassignedDateToShiftRecords: patch({
            [startDateTime]: patch({
              [action.shift.id]: action.shift,
            }),
          }),
        }),
      );
    }
  }

  @Action(UpdateShiftWithoutActivitiesFromUnassignedRegion)
  updateShiftWithoutActivitiesFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: UpdateShiftWithoutActivitiesFromUnassignedRegion,
  ): void {
    ctx.setState(
      patch({
        unassignedShiftsWithoutActivities: patch({
          [action.shift.id as string]: action.shift,
        }),
      }),
    );
  }

  @Action(RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion)
  removeCellViewShiftWithoutActivitiesFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: RemoveShiftWithoutActivitiesFromCellViewUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const clonedUnassignedDateToShiftRecords = {
      ...state.unassignedDateToShiftRecords,
    };
    const shift =
      ShiftTimelineUnassignedRegionState.getShiftFromUnassignedDateRecords(
        clonedUnassignedDateToShiftRecords,
        action.payload.shiftId,
      );

    if (shift) {
      const { startDatetime } = determineShiftRenderDatetime(shift);
      const startDateTime = toNumberOfShiftPerDayRecordKey(
        new Date(startDatetime.date),
      );

      const stackShifts = {
        ...clonedUnassignedDateToShiftRecords[startDateTime],
      };

      delete stackShifts[action.payload.shiftId];

      ctx.setState(
        patch({
          unassignedDateToShiftRecords: patch({
            [startDateTime]: stackShifts,
          }),
        }),
      );
    } else {
      this.toastrService.error(
        this.translateService.instant('general.something_went_wrong'),
      );
    }
  }

  @Action(RemoveShiftWithoutActivitiesFromUnassignedRegion)
  removeShiftWithoutActivitiesFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: RemoveShiftWithoutActivitiesFromUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const shifts = {
      ...state.unassignedShiftsWithoutActivities,
    };
    delete shifts[action.payload.shiftId];

    ctx.patchState({
      unassignedShiftsWithoutActivities: shifts,
    });
  }

  @Action(BulkDeleteShiftActivitiesFromUnassignedRegion)
  bulkDeleteShiftActivitiesToUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: BulkDeleteShiftActivitiesFromUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const shiftActivitiesRows = cloneDeep(state.shiftActivitiesRows);
    const activityRows = shiftActivitiesRows[action.shiftId];
    if (activityRows) {
      delete shiftActivitiesRows[action.shiftId];
    }
    ctx.setState(
      patch({
        shiftActivitiesRows: shiftActivitiesRows,
      }),
    );
  }

  @Action(BulkAddShiftActivitiesToUnassignedRegion)
  addShiftActivitiesToUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: BulkAddShiftActivitiesToUnassignedRegion,
  ): void {
    ctx.setState(
      patch({
        shiftActivitiesRows: patch({
          [action.shiftId]: action.activityRow,
        }),
      }),
    );
  }

  @Action(AddShiftWithoutActivitiesToCellViewUnassignedRegion)
  addShiftWithoutActivitiesToCellViewUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: AddShiftWithoutActivitiesToCellViewUnassignedRegion,
  ): void {
    const { startDatetime } = determineShiftRenderDatetime(action.shift);
    const startTime = toNumberOfShiftPerDayRecordKey(
      new Date(startDatetime.date),
    );

    ctx.setState(
      patch({
        unassignedDateToShiftRecords: patch({
          [startTime]: patch({
            [action.shift.id]: action.shift,
          }),
        }),
      }),
    );
  }

  @Action(AddShiftWithoutActivitiesToUnassignedRegion)
  addShiftWithoutActivitiesToUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: AddShiftWithoutActivitiesToUnassignedRegion,
  ): void {
    ctx.setState(
      patch({
        unassignedShiftsWithoutActivities: patch({
          [action.shift.id]: action.shift,
        }),
      }),
    );
  }

  @Action(TimelineUpsertActivityToUnassignedRegion)
  timelineUpsertActivityToUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineUpsertActivityToUnassignedRegion,
  ): void {
    const state = ctx.getState();
    let activityRows = state.shiftActivitiesRows[action.shiftId];

    if (!activityRows) {
      activityRows = [];
    }
    const allActivities = activityRows.reduce((acc, row) => {
      return { ...acc, ...row };
    }, {});

    if (action.activity.id) {
      allActivities[action.activity.id] = action.activity;
    }

    const determinedActivities =
      this.activitiesStateLoaderService.getActivityRows(
        Object.values(allActivities) as (Activity & { id: string })[],
      );

    ctx.setState(
      patch({
        shiftActivitiesRows: patch({
          [action.shiftId]: determinedActivities,
        }),
      }),
    );
  }

  @Action(TimelineRemoveActivityFromUnassignedRegion)
  timelineRemoveActivityFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: TimelineRemoveActivityFromUnassignedRegion,
  ): void {
    const state = ctx.getState();
    let activityRows = state.shiftActivitiesRows[action.shiftId];

    if (!activityRows) {
      activityRows = [];
    }
    const allActivities = activityRows.reduce((acc, row) => {
      return { ...acc, ...row };
    }, {});

    delete allActivities[action.activityId];

    const determinedActivities =
      this.activitiesStateLoaderService.getActivityRows(
        Object.values(allActivities) as (Activity & { id: string })[],
      );

    ctx.setState(
      patch({
        shiftActivitiesRows: patch({
          [action.shiftId]: determinedActivities,
        }),
      }),
    );
  }

  private fetchUnassignedShiftsAndPrepareDataForStandardView(
    timeFrame: TimelineTimeframe,
  ): Observable<Record<string, UnassignedShift>> {
    this.unsubRequestForUnassignedShiftsSubject.next(null);
    return this.getShiftsRequest(timeFrame).pipe(
      takeUntil(this.unsubRequestForUnassignedShiftsSubject),
      map((response) => {
        const transformedShifts = response.reduce(
          (record, shift) => {
            record[shift.id] = shift;
            return record;
          },
          {} as Record<ShiftId, UnassignedShift>,
        );

        return transformedShifts;
      }),
    );
  }

  fetchUnassignedShiftsAndPrepareDataForCellView(
    timeFrame: TimelineTimeframe,
  ): Observable<UnassignedDateToShiftRecords> {
    this.unsubRequestForUnassignedShiftsSubject.next(null);
    return this.getShiftsRequest(timeFrame).pipe(
      takeUntil(this.unsubRequestForUnassignedShiftsSubject),
      map((response) => {
        // TODO: create test for this algorithm
        if (response.length) {
          const transformedShifts = response.reduce((record, shift) => {
            const shiftStartDateKey = toNumberOfShiftPerDayRecordKey(
              new Date(getStartDatetime(shift).date),
            );

            record[shiftStartDateKey] = Object.assign(
              record[shiftStartDateKey] || {},
              {
                [shift.id as string]: shift,
              },
            );

            return record;
          }, {} as UnassignedDateToShiftRecords);

          return transformedShifts;
        } else {
          return {};
        }
      }),
    );
  }

  private fetchUnassignedShiftsCountPerDay(
    timeFrame: TimelineTimeframe,
  ): Observable<NumberOfShiftPerDayRecord> {
    this.unsubRequestForUnassignedShiftsSubject.next(null);

    return this.getUnassignedTimelinesShiftsCount(timeFrame).pipe(
      takeUntil(this.unsubRequestForUnassignedShiftsSubject),
      map((response) => {
        if (response.length) {
          return response.reduce((record, { date, numberOfShifts }) => {
            const startDateKey = toNumberOfShiftPerDayRecordKey(new Date(date));

            record[startDateKey] = numberOfShifts;
            return record;
          }, {} as NumberOfShiftPerDayRecord);
        } else {
          return {};
        }
      }),
    );
  }

  private getUnassignedTimelinesShiftsCount(
    timeFrame: TimelineTimeframe,
  ): Observable<{ date: string; numberOfShifts: number }[]> {
    const correctedInterval = getTimeZoneCorrectedDateRange(timeFrame);

    return from(
      this.shiftTimelineFilterStateService.generateShiftFilterOptionsForUnassignedRegion(
        correctedInterval.start,
        correctedInterval.end,
      ),
    ).pipe(
      switchMap((options) =>
        this.userTimelinesGateway.getUnassignedTimelinesShiftsCount(options),
      ),
    );
  }

  private getShiftsRequest(
    timeFrame: TimelineTimeframe,
  ): Observable<UnassignedShift[]> {
    const correctedInterval = getTimeZoneCorrectedDateRange(timeFrame);

    return from(
      this.shiftTimelineFilterStateService.generateShiftFilterOptionsForUnassignedRegion(
        correctedInterval.start,
        correctedInterval.end,
      ),
    ).pipe(
      switchMap((options) =>
        this.userTimelinesGateway.getUnassignedTimelinesShiftsV3(options),
      ),
    );
  }

  @Action(AddShiftTemplateToSuggestionList)
  addShiftTemplateToSuggestionList(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: AddShiftTemplateToSuggestionList,
  ): void {
    const state = ctx.getState();
    const suggestedShiftTemplates = cloneDeep(state.suggestedShiftTemplates);
    if (
      action.shiftTemplate &&
      suggestedShiftTemplates.findIndex(
        (template) => template.id === action.shiftTemplate?.id,
      ) === -1
    ) {
      if (suggestedShiftTemplates.length === 3) {
        suggestedShiftTemplates.shift();
      }
      suggestedShiftTemplates.push(action.shiftTemplate);
    }
    ctx.patchState({
      suggestedShiftTemplates,
    });
  }

  @Action(AddActivityToUnassignedRegion)
  addActivityToUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: AddActivityToUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const activities = cloneDeep(state.unassignedActivities);
    activities.push(action.activity);
    const updatablePartialState =
      this.timelineFetchUnassignedActivitiesActionService.getUpdatablePartialStateSync(
        activities,
      );
    ctx.patchState({
      ...updatablePartialState,
      unassignedActivities: activities,
    });
  }

  @Action(RemoveActivityFromUnassignedRegion)
  removeActivityFromUnassignedRegion(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    action: RemoveActivityFromUnassignedRegion,
  ): void {
    const state = ctx.getState();
    const activities = cloneDeep(state.unassignedActivities);
    const activityToRemoveIndex = activities.findIndex(
      (shift) => shift.id === action.activity.id,
    );
    activities.splice(activityToRemoveIndex, 1);

    const updatablePartialState =
      this.timelineFetchUnassignedActivitiesActionService.getUpdatablePartialStateSync(
        activities,
      );
    ctx.patchState({
      ...updatablePartialState,
      unassignedActivities: activities,
    });
  }

  @Action(FetchActivitiesForUnassignedShifts)
  fetchActivitiesForUnassignedShifts(
    ctx: StateContext<ShiftTimelineUnassignedRegionStateModel>,
    { shiftIds }: FetchActivitiesForUnassignedShifts,
  ): void {
    this.activitiesStateLoaderService.loadActivitiesToUnassignedRegionState(
      ctx,
      shiftIds,
    );
  }

  static getShiftFromUnassignedDateRecords(
    unassignedDateToShiftRecords: UnassignedDateToShiftRecords,
    shiftId: string,
  ): UnassignedShift | undefined {
    const dateRecords = Object.values(unassignedDateToShiftRecords);

    for (const shiftsById of dateRecords) {
      if (shiftsById[shiftId]) {
        return shiftsById[shiftId];
      }
    }
    return undefined;
  }
}
